<template>
  <div>
    <v-container v-if="mode === 'list'" fluid grid-list-md>
      <v-layout wrap align-center>
        <v-flex xs12 d-flex>
          <div>
            <other-menus-modal ref="otherMenusModal"></other-menus-modal>
            <menu-manage-modal
                :patient-id="patient_id"
                :add-button-enabled="true"
            />
            <permission-tooltip
                :permission="canImport"
                :tooltip="{top: true, bottom: false, attach: false}"
            >
              <import-dialog :patient-id="patient_id" @schemaSelected="schemaSelectedForImport"></import-dialog>
              <menu-scale-modal ref="menuScaleModal"></menu-scale-modal>
            </permission-tooltip>
          </div>
        </v-flex>
      </v-layout>
    </v-container>
    <v-data-table
        :items="menus.data"
        :disable-initial-sort="true"
        :headers="headers"
        :pagination.sync="pagination"
        :total-items="total"
        :loading="isLoading"
        :rows-per-page-items="[10,25,50,100]"
        :no-data-text="trans('app.search.no_data')"
    >
      <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
      <template slot="items" slot-scope="props">
        <tr>
          <td>
            {{ props.item.start_date | format_date() }}
          </td>
          <td>
            {{ props.item.end_date | format_date()}}
          </td>
          <td>
            {{ props.item.days }}
          </td>
          <td>
            {{ props.item.desired_diet_energy }}
          </td>
          <td>
            {{ props.item.created_at | format_date(FORMAT_DATETIME) }}
          </td>
          <td class="justify-end layout px-0 pr-4">
            <v-layout align-center justify-end v-if="mode === 'list'">
              <icon-button v-if="hasPermission('menu_schema_mobile_app_send') && props.item.sent_to_mobile_app"
                           icon="send_to_mobile"
                           :color="mobileAppAccess.status === 'app_configured' ? `success` : `orange`"
                           cls="mr-0 ml-0"
                           :permission="canSendToMobileApp()"
                           :tooltip="{top: true, bottom: false, attach: false}"
                           :iconOptions="{small: true}"
                           @click="updateInMobileApp(props.item)"
              >
                {{
                  trans(
                      mobileAppAccess.status === 'app_configured' ? 'app.mobile_app.update_menu' : 'app.mobile_app.update_menu_app_not_configured',
                      {'date': formatSentToMobileAppAt(props.item.sent_to_mobile_app_at)}
                  )
                }}
              </icon-button>
              <icon-button
                  v-if="hasPermission('menu_schema_mobile_app_send')"
                  :icon="props.item.sent_to_mobile_app ? 'mobile_off' : 'send_to_mobile'"
                  :color="props.item.sent_to_mobile_app ? `red`: `light-blue`"
                  cls="mr-0 ml-0"
                  :permission="canSendToMobileApp()"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
                  :no-permission-tool-tip="sendToMobileAppNoPermissionTooltip"
                  @click="toggleSendToMobileApp(props.item)"
              >
                {{ props.item.sent_to_mobile_app ? 'app.mobile_app.delete_menu' : 'app.mobile_app.send_menu' | trans }}
              </icon-button>
              <icon-button
                  icon="launch"
                  color="light-blue"
                  cls="mr-0 ml-0"
                  :permission="hasPermission('menu_schema_show')"
                  :href="`/menus/${props.item.uuid}`"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
              >
                {{ 'app.open' | trans}}
              </icon-button>
              <icon-button
                  :permission="canEdit(props.item)"
                  icon="edit"
                  color="success"
                  cls="mr-0 ml-0"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
                  :href="`/menus/${props.item.uuid}?open_edit_modal=true`"
              >
                {{ 'app.show_and_edit' | trans }}
              </icon-button>
              <icon-button
                  :permission="canDelete(props.item)"
                  icon="delete"
                  color="red"
                  cls="mr-0 ml-0"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
                  @click="deleteMenu(props.item)"
              >
                {{ 'app.delete' | trans }}
              </icon-button>
            </v-layout>
            <v-layout align-center justify-end v-if="mode === 'select'">
              <v-btn flat icon :color="`primary`"
                     :disabled="selectedCombinedMenus.length === 4"
                     @click="selectMenu(props.item)">
                <v-icon>done</v-icon>
              </v-btn>
            </v-layout>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import {trans} from "../../app";
import MenuService from "../../services/MenuService";
import goTo from "vuetify/lib/components/Vuetify/goTo";
import {mapActions, mapGetters, mapState} from "vuex";
import {dateFormatter, FORMAT_DATE, FORMAT_DATETIME} from "../../utils/dateFormatter";
import moment from "moment";
import {DIETITIAN_FREE, DIETITIAN_TEST} from "../../utils/roles";

export default {
  props: {
    patient_id: {
      type: Number,
    },
    mode: {
      type: String,
      default: 'list'
    }
  },
  data: function () {
    return {
      FORMAT_DATETIME: FORMAT_DATETIME,
      items: [],
      headers: [
        {text: trans('app.menu.start_date'), align: 'left', value: 'start_date', sortable: true,},
        {text: trans('app.menu.end_date'), align: 'left', value: 'end_date', sortable: false,},
        {text: trans('app.menu.days'), align: 'left', value: 'days', sortable: true,},
        {text: trans('app.menu.desired_diet_energy'), align: 'left', value: 'desired_diet_energy', sortable: true,},
        {text: trans('app.createdAt'), align: 'left', value: 'created_at', sortable: true,},
        {text: '', align: 'center', sortable: false,},
      ],
      pagination: {
        rowsPerPage: 50,
        sortBy: 'created_at',
        descending: true,
      },
      total: 0,
      isLoading: true,
      menuService: null,
    };
  },
  created() {
    this.menuService = new MenuService();
    this.triggerFetchMenus();
  },
  mounted() {
    if (this.hasPermission('patient_mobile_app_invite')) {
      this.fetchMobileAppAccess(this.patient_id);
    }
  },
  computed: {
    ...mapState([
        'menu'
    ]),
    ...mapGetters([
      'configuration',
      'hasPermission',
      'MENU_READ_ONLY',
      'MENU_MODE',
      'SYSTEM_SCHEMA',
      'commentsHidden',
      'foodsWithSearchedProduct',
      'productsSameAsSearchedProduct',
      'menus',
      'patient',
      'selectedCombinedMenus',
      'mobileAppAccess',
      'userRole',
      'patientMenusCount',
    ]),
    ...mapState({
      menu(state) {
        return state.menu;
      },
      days(state) {
        if (state.menu.schema)
          return state.menu.schema.days.sort((a, b) => a.date > b.date);
      },
      menuComment(state) {
        if (state.menu.schema) {
          return state.menu.schema.comment;
        }
      }
    }),
    period() {
      let period = dateFormatter(this.menu.startDate, FORMAT_DATE);

      if (this.days.length > 1)
        period += ' - ' + dateFormatter(this.menu.endDate, FORMAT_DATE);

      period += ' (' + this.menu.getNumberOfDays() + ' dni)';

      if (!this.isMenuMode) {
        period = this.menu.getNumberOfDays() + ' dni';
      }
      return period;
    },
    isMenuMode() {
      return this.configuration.mode === 'menu';
    },
    sendToMobileAppNoPermissionTooltip() {
      if (this.mobileAppAccess.status === 'to_invite') {
        return trans('app.mobile_app.patient_is_not_invited_to_mobile_app');
      }
      return trans('app.no_access_tooltip');
    },
    sendToMobileAppTooltip() {
      if (this.mobileAppAccess.status === 'invited' ||
          this.mobileAppAccess.status === 'accepted') {
        return trans('app.mobile_app.update_menu_app_not_configured')
      }
      return trans('app.mobile_app.update_menu')
    },
  },
  methods: {
    ...mapActions(['storeMenu', 'fetchMenu', 'fetchConfiguration', 'fetchMenus', 'fetchMobileAppAccess', 'fetchPatientMenusCount']),
    async fetchData() {
      this.isLoading = true;
      const {sortBy, descending, page, rowsPerPage} = this.pagination;
      const query = {
        page: page,
        perPage: rowsPerPage,
        orderBy: sortBy,
        dir: descending ? 'desc' : 'asc',
      };
      await this.triggerFetchMenus(query);
      this.isLoading = false;
    },
    deleteMenu(menu) {
      const startDateFormatted = moment(menu.start_date).format('DD.MM.YYYY');
      const endDateFormatted = moment(menu.end_date).format('DD.MM.YYYY');

      const message = trans('app.menu.confirm_menu_removal', {
        first_name: this.patient.first_name,
        last_name: this.patient.last_name,
        start_date: startDateFormatted,
        end_date: endDateFormatted,
        kcal: menu.desired_diet_energy
      });
      this.$confirmDanger(
          message,
          trans('app.menu.menu_remove')
      ).then(async () => {
        goTo(0, {
          duration: 1000
        });
        this.isLoading = true;
        try {
          await this.menuService.deleteMenu(this.patient_id, menu.id);
        } catch (error) {
          this.isLoading = false;
          if (error.response && error.response.status === 409) {
            this.$toast.error(trans('app.menu.unable_to_delete_sent_menu'));
          } else {
            this.sentryCaptureException(error);
          }
          return;
        }

        await this.triggerFetchMenus();
        this.$toast(trans('app.actions.removed', {name: trans('app.menu.menu').toLowerCase()}));
        this.isLoading = false;
        await this.fetchPatientMenusCount(this.patient_id);
      }).catch((error) => {
        this.sentryCaptureException(error);
      });
    },
    resetSearch() {
      this.pagination.sortBy = 'created_at';
      this.pagination.descending = true;
      this.pagination.rowsPerPage = 50;
    },
    async triggerFetchMenus(query = null, patientId = null) {
      let params = query || this.pagination;
      params.excluded_menus = this.selectedCombinedMenus.map((selectedMenu) => {
        return selectedMenu.uuid;
      });
      if (this.menu.id) {
        params.excluded_menus.push(this.menu.id);
      }
      await this.fetchMenus({
        patientId: patientId || this.patient_id,
        params: params
      });
      this.total = this.menus.total;
      this.pages = this.menus.last_page;
    },
    selectMenu(item) {
      this.$emit('menu-selected', item);
    },
    schemaSelectedForImport(payload) {
      this.$refs.menuScaleModal.open(this.patient_id, payload.id, payload.desiredDietEnergy);
    },
    canEdit(menu) {
      if (menu.days > 7 && !this.hasPermission('menu_schema_31_days')) {
        return false;
      }
      return this.hasPermission('menu_schema_edit');
    },
    canDelete(menu) {
      if (menu.days > 7 && !this.hasPermission('menu_schema_31_days')) {
        return false;
      }
      return this.hasPermission('menu_schema_delete');
    },
    canImport() {
      if ((this.userRole === DIETITIAN_FREE || this.userRole === DIETITIAN_TEST) && this.patientMenusCount >= 5) {
        return false;
      }
      return this.hasPermission('menu_schema_import');
    },
    canSendToMobileApp() {
      if (!this.hasPermission('menu_schema_mobile_app_send')) {
        return false;
      }

      return this.mobileAppAccess.status === 'invited' ||
             this.mobileAppAccess.status === 'accepted' ||
             this.mobileAppAccess.status === 'app_configured';
    },
    updateInMobileApp(menu) {
      const menuService = new MenuService();
      menuService.updateInMobileApp(this.patient_id, menu.id)
          .then(() => {
            this.$toast(trans('app.mobile_app.updated_menu'));
            this.fetchData();
          })
          .catch((error) => {
            if (error.response && error.response.status === 409) {
              this.$refs.otherMenusModal.open(menu.start_date, menu.end_date, error.response.data.data);
              return;
            }
            this.sentryCaptureException(error);
            this.$toast.error(trans('app.error_occurred'));
          });
    },
    toggleSendToMobileApp(menu) {
      const menuService = new MenuService();
      if (menu.sent_to_mobile_app) {
        this.$confirmDanger(trans('app.mobile_app.confirm_delete_menu')).then(() => {
          menuService.deleteFromMobileApp(this.patient_id, menu.id)
              .then(() => {
                this.$toast(trans('app.mobile_app.deleted_menu'));
                this.fetchData();
              });
        }).catch((error) => {
          this.sentryCaptureException(error);
        });
        return;
      }
      menuService.sendToMobileApp(this.patient_id, menu.id).then(() => {
        this.$toast(trans('app.mobile_app.sent_menu'));
        this.fetchData();
      }).catch((error) => {
        if (error.response && error.response.status === 409) {
          this.$refs.otherMenusModal.open(menu.start_date, menu.end_date, error.response.data.data);
          return;
        }
        this.sentryCaptureException(error);
        this.$toast.error(trans('app.error_occurred'));
      });
    },
    formatSentToMobileAppAt(date) {
      return dateFormatter(date, FORMAT_DATETIME);
    },
  },
  watch: {
    pagination: {
      handler() {
        this.fetchData();
      },
      deep: true,
    },
  },
}
</script>
